import React, { lazy, Suspense, useState, useMemo } from 'react';
import Parcel from 'single-spa-react/parcel';
import { property } from 'lodash';
import { catchSyncStacktrace } from 'auto-trace';
import { CprIcon, CpLoader } from 'canopy-styleguide!sofe';
import { useNav } from 'src/nav.context';
import { getGlobalPlusNavItems } from '../secondary-navs/global-plus-nav.helper';
import { featureEnabled } from 'feature-toggles!sofe';

import styles from './global-add-button.styles.css';

const TimeEntryModal = lazy(() =>
  SystemJS.import('billing-ui!sofe').then(billingUI =>
    billingUI.getTimeEntryModal().then(modal => ({ default: modal }))
  )
);

const InvoiceOverlay = lazy(() =>
  SystemJS.import('billing-ui!sofe').then(billingUI =>
    billingUI.getInvoiceOverlay().then(modal => ({ default: modal }))
  )
);

const ComposeEmail = lazy(() =>
  SystemJS.import('communications-ui!sofe').then(commsUI => commsUI.getComposeEmailOverlay())
);

const TranscriptsRequestDialog = lazy(() =>
  SystemJS.import('transcripts-ui!sofe').then(transcriptsUI => transcriptsUI.getTranscriptsRequestDialog())
);

const DocumentEditorOverlay = lazy(() =>
  SystemJS.import('document-editor-ui!sofe')
    .then(deUI => deUI.getDocumentEditorOverlay())
    .then(modal => modal)
);

const NewLetterModal = lazy(() =>
  SystemJS.import('document-editor-ui!sofe')
    .then(deUI => deUI.getNewLetterFromTemplateModal())
    .then(modal => modal)
);

export const GlobalAddButton = ({ showGlobalAddMenu }) => {
  const nav = useNav();

  const globalPlusNavItems = useMemo(() => {
    const navItems = getGlobalPlusNavItems(nav);
    return navItems.filter(link => link.enabled);
  }, [nav.user]);

  const [letterTemplate, setLetterTemplate] = useState();
  const [paymentData, setPaymentData] = useState();

  const globalActive = nav && nav.secondaryOpen && nav.secondaryOpen.id === 'globalplus';

  return (
    <>
      {globalPlusNavItems.length > 0 && (
        <a onClick={() => showGlobalAddMenu(!globalActive)}>
          <div className={`${styles.container} ${globalActive ? styles.active : ''}`}>
            <CprIcon name={globalActive ? 'remove-minus' : 'add-large'} size="23" />
          </div>
        </a>
      )}
      {nav && nav.openModal === 'task' && (
        <Parcel
          config={() => SystemJS.import('tasks-ui!sofe').then(taskUI => taskUI.getServiceModalParcel())}
          appendTo={document.body}
          handleError={catchSyncStacktrace}
          close={() => nav.setOpenModal()}
          taskUpdated={() => nav.setOpenModal()}
          contactIds={nav.clientId}
        />
      )}
      {nav && nav.openModal === 'contact' && (
        <Parcel
          config={() => SystemJS.import('contacts-ui!sofe').then(property('CreateEditContact'))}
          appendTo={document.body}
          handleError={catchSyncStacktrace}
          onModalHide={() => nav.setOpenModal()}
        />
      )}
      {nav && nav.openModal === 'clientRequest' && nav.dynamicLoadedModals.clientRequest && (
        <nav.dynamicLoadedModals.clientRequest onModalClose={() => nav.setOpenModal()} contactId={nav.clientId} />
      )}
      {nav && nav.openModal === 'email' && (
        <ComposeEmail
          close={() => {
            nav.setOpenModal();
          }}
        />
      )}
      {nav && featureEnabled('ft_react_letters') && (
        <Suspense fallback={<CpLoader />}>
          <NewLetterModal
            show={nav.openModal === 'letter'}
            onClose={template => {
              nav.setOpenModal();
              if (template) {
                setLetterTemplate(template);
                nav.setOpenModal('documentEditor');
              }
            }}
          />
        </Suspense>
      )}
      {nav && featureEnabled('ft_react_letters') && (
        <Suspense fallback={<CpLoader />}>
          <DocumentEditorOverlay
            template={letterTemplate}
            show={nav.openModal === 'documentEditor'}
            onClose={() => {
              nav.setOpenModal();
            }}
          />
        </Suspense>
      )}
      {nav && (
        <Suspense fallback={<CpLoader />}>
          <TimeEntryModal
            contactId={nav.clientId}
            task={nav.task}
            subtask={nav.subtask}
            onClose={() => nav.setOpenModal()}
            show={nav.openModal === 'timeEntry'}
          />
        </Suspense>
      )}
      {nav && nav.openModal === 'event' && (
        <Parcel
          config={() =>
            SystemJS.import('calendar-ui!sofe').then(calUI => {
              return calUI.getCreateEventParcel();
            })
          }
          appendTo={document.body}
          handleError={catchSyncStacktrace}
          contactId={nav.clientId}
          close={() => nav.setOpenModal()}
        />
      )}
      {nav && nav.openModal === 'organizer' && (
        <Parcel
          config={() => SystemJS.import('tax-prep-ui!sofe').then(tpui => tpui.createTaxReturn)}
          appendTo={document.body}
          handleError={catchSyncStacktrace}
          onModalClose={() => nav.setOpenModal()}
        />
      )}
      {nav && (
        <Suspense fallback={<CpLoader />}>
          <InvoiceOverlay
            overlayData={{ clientId: nav.clientId, mode: 'create' }}
            show={nav.openModal === 'invoice'}
            onClose={(addPayment = false, paymentData = {}) => {
              nav.setOpenModal();
              if (addPayment) {
                setPaymentData(paymentData);
                nav.setOpenModal('payment');
              }
            }}
          />
        </Suspense>
      )}
      {nav && nav.openModal === 'payment' && (
        <Parcel
          config={() => SystemJS.import('billing-ui!sofe').then(bui => bui.fetchCreatePaymentParcel())}
          appendTo={document.body}
          paymentData={paymentData || { clientId: nav.clientId }}
          handleError={catchSyncStacktrace}
          onModalClose={() => {
            nav.setOpenModal();
            setPaymentData(null);
          }}
        />
      )}
      {nav && nav.openModal === 'notice' && (
        <Parcel
          config={() => SystemJS.import('notices-ui!sofe').then(property('createNoticeParcel'))}
          appendTo={document.body}
          handleError={catchSyncStacktrace}
          onModalClose={() => nav.setOpenModal()}
          clientId={nav.clientId}
        />
      )}
      {nav && (
        <Suspense fallback={<CpLoader />}>
          <TranscriptsRequestDialog
            show={nav.openModal === 'transcripts'}
            onClose={() => nav.setOpenModal()}
            contactId={nav.clientId}
          />
        </Suspense>
      )}
    </>
  );
};
